import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DockNote } from './DockNote';
import { ReplyChip } from './ReplyChip';
import { ThinkingDots } from './ThinkingDots';
import { useQuest } from '../hooks/useQuest';
import { quest, SHARD_COUNT } from '../utils/quest';
import { dockBox } from '../utils/dock';
import { questEventLines, questHints, questStartTip } from '../data/questScript';
import type { BotLine, ChatOption, ChatPhase } from '../types/chat';
import type { Emotion } from '../types/emotion';
interface QuestHudProps {
  reduce: boolean;
  onPhase: (phase: ChatPhase, emotion: Emotion) => void;
  onPulse: () => void;
  /** The HUD is in use — the normal corner chat steps aside */
  onBusyChange: (busy: boolean) => void;
  /** Pixel is sitting in the chat corner right now (he only ever sends messages from there) */
  inCorner: boolean;
}
interface Note {
  id: number;
  text: string;
}
const MAX_STACK = 4;
/** Phones: the corner chat's thinking slot under the notes (same as DockChat) */
const THINK_SLOT = 28;
/** No progress for this long → Pixel drops the next hint by himself (ms) */
const AUTO_HINT_AFTER = 32000;
/** After the quest ends, his last notes stay up this long (ms) */
const END_LINGER = 9000;
const EASE = [0.23, 1, 0.32, 1] as const;
type Confirm = 'none' | 'leave';

/**
 * Quest mode's chat corner: Pixel's messages (grey notes), the shard counter and the quest controls.
 * He sits here for the whole hunt; messages wait until he has landed in the corner.
 */
export function QuestHud({
  reduce,
  onPhase,
  onPulse,
  onBusyChange,
  inCorner
}: QuestHudProps) {
  const state = useQuest();
  const [notes, setNotes] = useState<Note[]>([]);
  const [thinking, setThinking] = useState(false);
  const [confirm, setConfirm] = useState<Confirm>('none');
  const [lingering, setLingering] = useState(false);
  const [vw, setVw] = useState(() => typeof window === 'undefined' ? 1280 : window.innerWidth);
  const queue = useRef<BotLine[]>([]);
  const running = useRef(false);
  const idRef = useRef(0);
  const hintIdx = useRef<Record<string, number>>({});
  const lastProgress = useRef(Date.now());
  const inCornerRef = useRef(inCorner);
  inCornerRef.current = inCorner;
  const cb = useRef({
    onPhase,
    onPulse
  });
  cb.current = {
    onPhase,
    onPulse
  };
  const active = state.status === 'active';
  const live = active || lingering;
  const [vh, setVh] = useState(() => typeof window === 'undefined' ? 800 : window.innerHeight);
  /** Phones: tap Pixel to tuck his notes and the quest controls away; tap again to bring them back */
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const onResize = () => {
      setVw(window.innerWidth);
      setVh(window.innerHeight);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  useEffect(() => onBusyChange(live), [live, onBusyChange]);
  // A fresh quest always starts with his chat showing
  useEffect(() => {
    if (!active) setCollapsed(false);
  }, [active]);

  // He leaves the corner (restoration) → the corner interface clears
  useEffect(() => {
    if (!inCorner && !running.current) {
      setNotes([]);
      setThinking(false);
    }
  }, [inCorner]);

  // Plays queued lines one by one — but only once he's landed in the corner
  const pump = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
    while (queue.current.length) {
      while (!inCornerRef.current) await sleep(120);
      const line = queue.current.shift()!;
      setThinking(true);
      cb.current.onPhase('thinking', 'curious');
      await sleep(reduce ? 150 : 380 + Math.min(line.text.length * 12, 600));
      setThinking(false);
      setNotes(n => [...n, {
        id: ++idRef.current,
        text: line.text
      }].slice(-MAX_STACK));
      cb.current.onPhase('speaking', line.emotion);
      cb.current.onPulse();
      await sleep(reduce ? 200 : 700 + Math.min(line.text.length * 14, 900));
      cb.current.onPhase('reacting', line.emotion);
    }
    cb.current.onPhase('listening', 'curious');
    running.current = false;
  }, [reduce]);
  const say = useCallback((lines: BotLine[], interrupt = false) => {
    if (!lines.length) return;
    if (interrupt) queue.current = [];
    queue.current.push(...lines);
    pump();
  }, [pump]);
  const nextHint = useCallback(() => {
    const step = quest.getState().step;
    const hints = questHints[step];
    if (!hints.length) return;
    const i = hintIdx.current[step] ?? 0;
    say([hints[Math.min(i, hints.length - 1)]], true);
    hintIdx.current[step] = i + 1;
    lastProgress.current = Date.now();
  }, [say]);

  // Every quest event → Pixel comments on it (from the corner)
  useEffect(() => {
    const ev = state.event;
    if (ev === 'none') return;
    const lines = questEventLines[ev];
    if (!lines) return;
    lastProgress.current = Date.now();
    setConfirm('none');
    if (ev === 'start') {
      setNotes([]);
      setLingering(false);
    }
    if (ev === 'complete' || ev === 'quit') setLingering(true);
    // Phones: right at the start he mentions he can be tapped to tuck the chat away (and back)
    const withTip: BotLine[] = ev === 'start' && window.innerWidth < 768 ? [lines[0], ...questStartTip, ...lines.slice(1)] : lines;
    say(withTip, true);
    // state.tick: repeated events must fire again
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tick]);

  // Stuck for a while → a gentle hint (he comes back to the corner to give it)
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => {
      const s = quest.getState();
      if (s.step === 'finale' || s.step === 'restore' || running.current) return;
      if (Date.now() - lastProgress.current > AUTO_HINT_AFTER && (hintIdx.current[s.step] ?? 0) < 3) nextHint();
    }, 2000);
    return () => window.clearInterval(id);
  }, [active, nextHint]);

  // Quest over: last notes linger, then the normal corner chat returns
  useEffect(() => {
    if (!lingering) return;
    const id = window.setTimeout(() => {
      setLingering(false);
      setNotes([]);
      cb.current.onPhase('idle', 'neutral');
    }, END_LINGER + 6000);
    return () => window.clearTimeout(id);
  }, [lingering]);
  const options: ChatOption[] = !active ? [] : confirm === 'leave' ? [{
    label: 'Yes, leave',
    next: 'leave',
    reaction: 'sad'
  }, {
    label: 'Keep going',
    next: 'stay',
    reaction: 'happy'
  }] : state.step === 'restore' || state.step === 'finale' ? [] // Restoration: Pixel shows you what to do, no buttons
  : [{
    label: 'Hint, please',
    next: 'hint',
    reaction: 'curious'
  }, {
    label: 'Leave quest',
    next: 'ask-leave',
    reaction: 'sad'
  }];
  const choose = (o: ChatOption) => {
    onPhase('reacting', o.reaction);
    if (o.next === 'hint') nextHint();else if (o.next === 'ask-leave') {
      setConfirm('leave');
      say([{
        text: 'Leave the quest? Really?',
        emotion: 'sad'
      }, {
        text: 'My shards will miss you.',
        emotion: 'curious'
      }], true);
    } else if (o.next === 'leave') quest.quit();else if (o.next === 'stay') {
      setConfirm('none');
      say([{
        text: 'YES. Knew you were a hero.',
        emotion: 'happy'
      }], true);
    }
  };

  // Restoration has its own notes; while he's out flying, the corner interface is gone
  if (!live || active && (state.step === 'finale' || state.step === 'restore') || !inCorner) return null;
  const {
    size,
    margin
  } = dockBox(vw);
  const narrow = vw < 768;
  const notesRight = margin + size * 0.98 + 6;
  const room = Math.max(180, vw - notesRight - 16);
  // Phones: tapping Pixel tucks everything away (and back)
  const hidden = narrow && collapsed;
  const shown = hidden ? [] : notes.slice(-(narrow && vh < 700 ? 3 : MAX_STACK));
  const showControls = active && !hidden;
  const shardPill = <div className="flex items-center gap-1.5 rounded-full bg-black px-[0.85em] py-[0.42em] text-[clamp(0.85rem,calc(1*var(--vw)),1.125rem)] font-semibold tracking-[-0.015em] text-white" aria-label={`${state.shards} of ${SHARD_COUNT} shards found`} data-id="element-227" data-mp="1utmwmt6xx90w">
      {Array.from({
      length: SHARD_COUNT
    }, (_, i) => <span key={i} aria-hidden="true" className={`h-2.5 w-2.5 rotate-45 rounded-[2px] transition-colors duration-300 ease-out ${i < state.shards ? 'bg-[#1A2FFB] ring-1 ring-white' : 'bg-white/25'}`} data-id="element-228" data-mp="173iy308lzzgr" />)}
      <span className="ml-1 tabular-nums" data-id="element-229" data-mp="1260wxw0u2ojb">
        {state.shards}/{SHARD_COUNT}
      </span>
    </div>;

  // Phones: exactly the corner chat's layout and sizes — compact grey notes up and to the left of
  // his head (tail pointing down at him), the thinking bubble in its own slot right under them, and
  // the shard counter + replies in a row beneath, beside Pixel
  if (narrow) {
    const mNotesBottom = margin + size * 0.5 + THINK_SLOT;
    return <div className="pointer-events-none fixed inset-0 z-[80]" aria-live="off" data-id="element-230" data-mp="12gkpyr7mfl68">
        <button type="button" aria-label={collapsed ? 'Show Pixel’s quest chat' : 'Hide Pixel’s quest chat'} aria-expanded={!collapsed} onClick={() => setCollapsed(c => !c)} className="pointer-events-auto absolute touch-manipulation rounded-full [-webkit-tap-highlight-color:transparent] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black" style={{
        right: margin - 12,
        bottom: margin - 12,
        width: size + 24,
        height: size + 24
      }} data-id="element-231" data-mp="xi9iuo8pnorp" />
        <div role="log" aria-live="polite" aria-label="Pixel says" className="absolute flex w-max flex-col items-end gap-[5px]" style={{
        right: notesRight,
        bottom: mNotesBottom
      }} data-id="element-232" data-mp="eqj4ewhkwvd5">
          <AnimatePresence initial={false} data-id="element-233" data-mp="xjnqr628v40">
            {shown.map((n, i) => <DockNote key={n.id} text={n.text} tail={i === shown.length - 1} reduce={reduce} data-id="element-234" data-mp="vqhve39uex2" data-mpi={i} />)}
          </AnimatePresence>
        </div>
        <div className="absolute flex origin-bottom-right scale-[0.99] justify-end" style={{
        right: notesRight,
        bottom: margin + size * 0.5
      }} data-id="element-235" data-mp="tna6xemjun6z">
          <AnimatePresence data-id="element-236" data-mp="job5ag07brth">
            {thinking && !hidden && <ThinkingDots key="think" reduce={reduce} tail="bottom-right" data-id="element-237" data-mp="jnt3z4yomnep" />}
          </AnimatePresence>
        </div>
        <AnimatePresence data-id="element-238" data-mp="39b0g2b41izzk">
          {showControls && <motion.div key="controls" className="absolute flex origin-top-right scale-[0.92] flex-row flex-wrap items-start justify-end gap-[5px]" style={{
          right: notesRight,
          top: `calc(100% - ${mNotesBottom - 14}px)`,
          width: 'max-content',
          maxWidth: Math.min(460, vw - notesRight - 16)
        }} initial={reduce ? false : {
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          transition: {
            duration: 0.15
          }
        }} transition={{
          duration: 0.24,
          ease: EASE
        }} data-id="element-239" data-mp="34d8stuecc21p">
              {shardPill}
              <AnimatePresence initial={false} data-id="element-240" data-mp="3myr5j6e9l07b">
                {options.map((o, i) => <ReplyChip key={`${confirm}-${state.step}-${o.label}`} option={o} index={i} count={options.length} picked={false} disabled={false} reduce={reduce} onChoose={choose} data-id="element-241" data-mp="3ryn60vnr69i4" data-mpi={i} />)}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  return (
    // Topmost chat layer, one bottom-anchored column: notes → thinking slot → controls (nothing overlaps)
    <div className="pointer-events-none fixed inset-0 z-[80]" aria-live="off" data-id="element-242" data-mp="1uhg6pgncpzlh">
      <div className="absolute flex flex-col items-end gap-[8px]" style={{
        right: notesRight,
        bottom: margin + size * 0.08,
        maxWidth: room
      }} data-id="element-243" data-mp="28hqxkjonve6n">
        <div role="log" aria-live="polite" aria-label="Pixel says" className="flex flex-col items-end gap-[6px]" data-id="element-244" data-mp="23imodt1qlggb">
          <AnimatePresence initial={false} data-id="element-245" data-mp="2ia519hzauuvg">
            {shown.map((n, i) => <DockNote key={n.id} text={n.text} tail={i === shown.length - 1} reduce={reduce} large maxWidth={Math.min(420, room)} data-id="element-246" data-mp="2d9hcl2yjmq4e" data-mpi={i} />)}
          </AnimatePresence>
        </div>

        <div className="flex h-[34px] items-center justify-end" data-id="element-247" data-mp="2n8g9xpduyzc3">
          <AnimatePresence data-id="element-248" data-mp="35uxn2icd28up">
            {thinking && !hidden && <motion.div key="think" className="rounded-full shadow-[0_2px_10px_rgba(0,0,0,0.12)]" exit={{
              opacity: 0,
              transition: {
                duration: 0.12
              }
            }} data-id="element-249" data-mp="35tmiwhf06z32">
                <ThinkingDots reduce={reduce} tail="bottom-right" data-id="element-250" data-mp="2wtzfjq2prj7d" />
              </motion.div>}
          </AnimatePresence>
        </div>

        <AnimatePresence data-id="element-251" data-mp="31voq74m0xuux">
          {showControls && <motion.div key="controls" className="flex flex-wrap items-center justify-end gap-[5px]" initial={reduce ? false : {
            opacity: 0,
            y: 6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            transition: {
              duration: 0.15
            }
          }} transition={{
            duration: 0.24,
            ease: EASE
          }} data-id="element-252" data-mp="2n467gz1s5m7v">
              {shardPill}
              <AnimatePresence initial={false} data-id="element-253" data-mp="246lrkhgztxze">
                {options.map((o, i) => <ReplyChip key={`${confirm}-${state.step}-${o.label}`} option={o} index={i} count={options.length} picked={false} disabled={false} reduce={reduce} onChoose={choose} data-id="element-254" data-mp="293ve9pczzvjj" data-mpi={i} />)}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>
  );
}